import React from 'react';
import Select, { MultiValue, SingleValue, StylesConfig } from 'react-select';
import './styles.scss';
import CustomOption from './CustomOption/CustomOption';

type OptionVisualStyle = {
	backgroundColor?: string;
	color?: string;
};

type SourceOption = {
	[key: string]: any;
	styles?: OptionVisualStyle;
	itemIcon?: string;
	isSeparator?: boolean;
};

type OptionType = {
	value: string | number;
	label: string;
	clearableValue?: boolean;
	className?: string;
	styles?: OptionVisualStyle;
	itemIcon?: string;
	isSeparator?: boolean;
	icon?: string;
};

interface MultiSelectBoxProps {
	name: string;
	label?: string;
	idField: string;
	valueField: string;
	className?: string;
	options: SourceOption[];
	selectedValues: string; // comma-separated ids
	onChange: (value: string, name: string) => void;
	onInputKeyDown?: (e: React.KeyboardEvent) => void;
	onInputChange?: (value: string) => void;
	isRequired?: boolean;
	errorMessage?: string;
	isMulti: boolean;
	defaultValue?: string; // kept for compatibility; passed through to Select as-any
	placeholder?: string;
	isDisabled?: boolean;
	isSearchable?: boolean;
	isFocused?: boolean;
	optionComponent?: React.ComponentType<any>;
	useFixedMenuPosition?: boolean;
}

interface MultiSelectBoxState {
	iOS: boolean;
	hasSubmitted: boolean; // kept for parity even if unused
	formValidated: boolean;
}

class MultiSelectBoxV6 extends React.Component<MultiSelectBoxProps, MultiSelectBoxState> {
	private containerRef = React.createRef<HTMLDivElement>();
	private inputRef: HTMLInputElement | null = null;
	private observer?: MutationObserver;
	private createdStyleSheets = new Map<string, HTMLStyleElement>();
	private styleCache = new Map<string, string>();
	private activeStyleNames = new Set<string>();

	constructor(props: MultiSelectBoxProps, context?: unknown) {
		super(props, context);
		this.state = {
			iOS: false,
			hasSubmitted: false,
			formValidated: false,
		};
	}

	componentDidMount() {
		const iOS = !!(navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform));
		this.setState({ iOS });

		this.attachFormValidationObserver();

		// Delay to ensure react-select renders the internal input
		setTimeout(() => {
			if (this.inputRef) {
				this.inputRef.addEventListener('paste', this.handlePaste);
			}
		}, 100);
	}

	componentWillUnmount() {
		this.removeFormValidationObserver();
		if (this.inputRef) {
			this.inputRef.removeEventListener('paste', this.handlePaste);
		}
		// Clean up all dynamically created style elements
		this.createdStyleSheets.forEach(styleSheet => {
			if (styleSheet.parentNode) {
				styleSheet.parentNode.removeChild(styleSheet);
			}
		});
		this.createdStyleSheets.clear();
		this.styleCache.clear();
		this.activeStyleNames.clear();
	}

	/** Observe the form for class changes (e.g., Bootstrap `was-validated`) */
	private attachFormValidationObserver = () => {
		const form = this.containerRef.current?.closest('form');
		if (form) {
			this.observer = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					const target = mutation.target as HTMLElement;
					if (target.classList.contains('was-validated')) {
						this.setState({ formValidated: true });
					}
				});
			});
			this.observer.observe(form, { attributes: true, attributeFilter: ['class'] });
		}
	};

	/** Remove observer when component unmounts */
	private removeFormValidationObserver = () => {
		if (this.observer) {
			this.observer.disconnect();
		}
	};

	private handlePaste = (e: ClipboardEvent) => {
		const pastedText = (e.clipboardData || (window as any).clipboardData)?.getData('text') ?? '';

		const abbreviations = pastedText
			.split(/[\s,;\n\t]+/)
			.map((x) => x.trim().toLowerCase())
			.filter(Boolean);

		const options = this.props.options || [];

		// options that match the abbreviations
		const matched = options
			.filter((opt) => {
				const val = (opt[this.props.valueField] ?? '').toString().toLowerCase();
				return abbreviations.some((abbr) => val.includes(abbr));
			})
			.map((opt) => opt[this.props.idField]?.toString());

		if (matched.length > 0) {
			const current = (this.props.selectedValues || '').split(',').filter(Boolean);
			const updated = Array.from(new Set([...current, ...matched]));
			this.props.onChange(updated.join(','), this.props.name);
			e.preventDefault();
		} else {
			// eslint-disable-next-line no-console
			console.log('❌ No matches found.');
		}
	};

	private isMultiValue = (value: MultiValue<OptionType> | SingleValue<OptionType>): value is MultiValue<OptionType> => {
		return Array.isArray(value);
	};

	private handleReactSelectChange = (selectedOption: MultiValue<OptionType> | SingleValue<OptionType>) => {
		if (this.props.isMulti) {
			// For multi-select, we expect an array
			const options = this.isMultiValue(selectedOption) ? selectedOption : [];
			const selectedValue = options.map((o) => o.value).join(',');
			this.props.onChange(selectedValue, this.props.name);
		} else {
			// For single-select, we expect a single option or null
			const option = !this.isMultiValue(selectedOption) ? selectedOption : null;
			const selectedValue = option?.value ? String(option.value) : '';
			this.props.onChange(selectedValue, this.props.name);
		}
	};

	private handleDomChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const selected = Array.from(e.target.options)
			.filter((o) => o.selected)
			.map((o) => o.value);
		this.props.onChange(selected.join(), this.props.name);
	};

	private handleKeyDown = (e: React.KeyboardEvent) => {
		if (this.props.onInputKeyDown) {
			this.props.onInputKeyDown(e);
		}
	};

	private onInputChange = (value: string) => {
		if (this.props.onInputChange) {
			this.props.onInputChange(value);
		}
	};

	private sanitizeColorValue = (color: string | undefined): string => {
		if (!color) return 'transparent';
		
		// Whitelist valid CSS color formats
		const validColorRegex = /^(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)|[a-zA-Z]+|transparent|inherit|initial|unset)$/;
		
		if (!validColorRegex.test(color.trim())) {
			console.warn(`Invalid color value detected and sanitized: ${color}`);
			return 'transparent';
		}
		
		return color.trim();
	};

	private createStyleForOption = (styleName: string, styles: OptionVisualStyle): void => {
		// Escape the style name to prevent CSS injection
		const escapedStyleName = CSS.escape(styleName);
		
		// Sanitize color values to prevent XSS
		const backgroundColor = this.sanitizeColorValue(styles.backgroundColor) || 'transparent';
		const color = this.sanitizeColorValue(styles.color) || 'inherit';
		
		// Check if we've already created this style
		const css = `
			.${escapedStyleName} {
				background-color: ${backgroundColor} !important;
				color: ${color} !important;
			}
		`;
		// Reuse the existing style only when the generated CSS is unchanged.
		if (this.styleCache.get(styleName) === css) {
			this.activeStyleNames.add(styleName);
			return;
		}
		let styleSheet = this.createdStyleSheets.get(styleName);
		if (!styleSheet) {
			styleSheet = document.createElement('style');
			styleSheet.setAttribute('data-multiselect-style', styleName);
			document.body.appendChild(styleSheet);
			this.createdStyleSheets.set(styleName, styleSheet);
		}
		// Use textContent instead of innerHTML to prevent XSS
		styleSheet.textContent = css;
		this.styleCache.set(styleName, css);
		this.activeStyleNames.add(styleName);
	};

	private cleanupUnusedStyles = (): void => {
		// Remove styles that are no longer active
		const stylesToRemove: string[] = [];
		this.createdStyleSheets.forEach((styleSheet, styleName) => {
			if (!this.activeStyleNames.has(styleName)) {
				if (styleSheet.parentNode) {
					styleSheet.parentNode.removeChild(styleSheet);
				}
				stylesToRemove.push(styleName);
			}
		});
		
		// Clean up maps
		stylesToRemove.forEach(styleName => {
			this.createdStyleSheets.delete(styleName);
			this.styleCache.delete(styleName);
		});
	};

	render() {
		const className = this.props.className || '';
		const isSingleSelect = !this.props.isMulti;

		// Show error only after form is validated
		const showError = this.state.formValidated && !!this.props.isRequired && (this.props.selectedValues || '').trim().length === 0;

		const invalidClass = showError ? 'is-invalid' : '';

		let multiSelect: React.ReactNode = null;

		if (this.state.iOS) {
			const values: string | string[] = this.props.isMulti ? (this.props.selectedValues ?? '').split(',') : this.props.selectedValues ?? '';

			const mobilePlaceholder = this.props.placeholder || 'Select...';

			multiSelect = (
				<select
					className='pacer-multi-select'
					multiple={this.props.isMulti}
					id={this.props.name}
					name={this.props.name}
					onChange={this.handleDomChange}
					value={values}
					required={this.props.isRequired}>
					<option disabled value=''>
						{mobilePlaceholder}
					</option>
					{this.props.options.map((option) => (
						<option key={option[this.props.idField]} value={option[this.props.idField]}>
							{option[this.props.valueField]}
						</option>
					))}
				</select>
			);
		} else {
			// Clear active styles for this render
			this.activeStyleNames.clear();

			// Build react-select options with custom styling classes based on stable option IDs
			const options: OptionType[] = this.props.options.map((option) => {
				const optionId = option[this.props.idField];
				// Escape the style name components to prevent CSS injection
				const styleName = `${CSS.escape(this.props.name)}_optionStyle_${CSS.escape(String(optionId))}`;
				if (option.styles) {
					this.createStyleForOption(styleName, option.styles);
				}
				return {
					value: option[this.props.idField],
					label: option[this.props.valueField],
					clearableValue: true,
					className: styleName,
					styles: option.styles || {},
					itemIcon: option.itemIcon,
					isSeparator: option.isSeparator,
				};
			});

			// Clean up any styles that are no longer being used
			this.cleanupUnusedStyles();

			const closeOnSelect = isSingleSelect;
			const clearable = this.props.isMulti;
			const searchable = this.props.isSearchable !== undefined ? this.props.isSearchable : true;

			const placeholder = this.props.placeholder || (this.props.isMulti ? 'Select all that apply...' : undefined);

			// react-select custom styles
			const zIndex = 99999;
			const customStyles: StylesConfig<OptionType, boolean> = {
				control: (base, state) => ({
					...base,
					borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
					boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
					'&:hover': { borderColor: '#ced4da' },
				}),
				menu: (base) => ({
					...base,
					zIndex: zIndex,
				}),
				menuPortal: (base) => ({
					...base,
					zIndex: zIndex,
				}),
				placeholder: (base) => ({
					...base,
					color: '#6c757d',
				}),
				option: (base, { data, isFocused, isSelected }) => ({
					...base,
					backgroundColor: isSelected
						? data.styles?.backgroundColor || '#007bff'
						: isFocused
							? '#f8f9fa'
							: data.styles?.backgroundColor || 'white',
					color: isSelected ? data.styles?.color || 'white' : data.styles?.color || '#212529',
					'&:active': {
						backgroundColor: data.styles?.backgroundColor || '#0056b3',
					},
				}),
				singleValue: (base, { data }) => ({
					...base,
					color: data.styles?.color || (base as any).color,
				}),
				multiValue: (base, { data }) => ({
					...base,
					backgroundColor: data.styles?.backgroundColor || '#e2e3e5',
					color: data.styles?.color || '#212529',
				}),
				multiValueLabel: (base, { data }) => ({
					...base,
					color: data.styles?.color || '#212529',
				}),
				multiValueRemove: (base, { data }) => ({
					...base,
					color: data.styles?.color || '#212529',
					'&:hover': {
						backgroundColor: data.styles?.backgroundColor || '#d6d8db',
						color: data.styles?.color || '#212529',
					},
				}),
				indicatorSeparator: () => ({
					display: 'none',
				}),
			};

			// Normalize selectedValues for react-select
			const normalizedValues: MultiValue<OptionType> | SingleValue<OptionType> = this.props.isMulti
				? ((this.props.selectedValues ?? '')
					.split(',')
					.map((id) => {
						if (!id) return null;
						const opt = options.find((o) => o.value !== undefined && o.value?.toString() === id.toString());
						return opt || null;
					})
					.filter(Boolean) as OptionType[])
				: options.find((o) => o.value !== undefined && o.value?.toString() === (this.props.selectedValues ?? '').toString()) || null;

			// Map options for react-select (preserving extra fields)
			const selectOptions: OptionType[] = options.map((option) => ({
				...option,
				value: option.value,
				label: option.label,
				icon: option.itemIcon,
				styles: option.styles,
			}));

			multiSelect = (
				<Select<OptionType, boolean>
					className={`pacer-multi-select ${invalidClass}`}
					name={this.props.name}
					value={normalizedValues as any}
					onChange={this.handleReactSelectChange as any}
					closeMenuOnSelect={closeOnSelect}
					isSearchable={searchable}
					isClearable={clearable}
					isMulti={this.props.isMulti}
					options={selectOptions}
					styles={customStyles}
					menuPortalTarget={document.body}
					{...(this.props.useFixedMenuPosition ? { menuPosition: 'fixed' } : {})}
					required={this.props.isRequired}
					onInputChange={(val) => {
						this.onInputChange(val);
						return val;
					}}
					defaultValue={this.props.defaultValue as any} // keep compatibility with original API
					placeholder={placeholder}
					isDisabled={this.props.isDisabled}
					autoFocus={this.props.isFocused}
					components={{ Option: this.props.optionComponent || (CustomOption as any) }}
					onKeyDown={this.handleKeyDown}
					ref={(ref) => {
						// Access internal input ref (react-select instance) – not officially typed
						if (ref && !this.inputRef) {
							setTimeout(() => {
								const inputEl = (ref as any)?.inputRef as HTMLInputElement | undefined;
								if (inputEl && !this.inputRef) {
									this.inputRef = inputEl;
									this.inputRef.addEventListener('paste', this.handlePaste);
								}
							}, 0);
						}
					}}
				/>
			);
		}

		return (
			<div className={`${className || 'mb-3'}`} ref={this.containerRef}>
				<div className='select-wrapper'>
					{this.props.label && (
						<label htmlFor={this.props.name} className='select-label'>
							{this.props.label}
						</label>
					)}
					{multiSelect}
					{showError && <div className='invalid-feedback'>{this.props.errorMessage}</div>}
				</div>
			</div>
		);
	}
}

export default MultiSelectBoxV6;
